import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ChinaLanding from './ChinaLanding'
import { CompatibilityModal } from '../components/CompatibilityModal'
import { CoverageModal } from '../components/CoverageModal'
import { Icon } from '../components/Icon'
import { useCatalog } from '../hooks/useCatalog'
import { useCountriesMeta } from '../hooks/useCountriesMeta'
import { track } from '../api/track'
import { gtagConversion } from '../api/gtag'
import { oaiqPlanClicked } from '../api/oaiq'
import {
  COUNTRIES,
  REGION_COVERAGE,
  REGIONAL_PLANS_META,
  formatData,
  isUnlimited,
  plansForCountry,
  priceDollars,
  type Plan,
} from '../data/catalog'
import { languageFromPath, withLang } from '../i18n/i18n'
import { SITE_ORIGIN, useDocumentMeta } from '../i18n/useDocumentMeta'
import { useStructuredData } from '../i18n/useStructuredData'
import { notePlanClick, useCheckoutWarmup, warmCheckout } from '../lib/checkoutWarmup'

interface DestinationMeta {
  code: string
  name: string
  flag: string
  networks: string
  // Per-country carrier-tech override; absent = the site-wide default.
  speed?: string
  isRegional: boolean
  scope?: string
  desc?: string
  image?: string
  coverageUnit?: 'countries' | 'regions'
  artwork?: boolean
  network_note?: string
}

function resolveMeta(codeParam: string | undefined): DestinationMeta | null {
  if (!codeParam) return null
  const upper = codeParam.toUpperCase()
  const country = COUNTRIES.find((c) => c.code === upper)
  if (country) {
    return {
      code: country.code,
      name: country.name,
      flag: country.flag,
      networks: country.networks,
      speed: country.speed,
      isRegional: false,
      image: country.image,
      network_note: country.network_note,
    }
  }
  const regional = REGIONAL_PLANS_META.find((r) => r.code === upper)
  if (regional) {
    return {
      code: regional.code,
      name: regional.name,
      flag: regional.flag,
      networks: regional.networks,
      isRegional: true,
      scope: regional.scope,
      desc: regional.desc,
      image: regional.image,
      coverageUnit: regional.coverageUnit,
      artwork: regional.artwork,
    }
  }
  return null
}

const REGIONAL_UPSELL: Record<string, string> = {
  JP: 'AP',
  KR: 'AP',
  CN: 'CHM',
}

function DestinationDetail() {
  const { country: codeParam } = useParams<{ country: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { plans, loading } = useCatalog()
  const { t } = useTranslation('detail')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  const [tab, setTab] = useState<'regular' | 'unlimited'>('regular')
  const [compatOpen, setCompatOpen] = useState(false)
  const [coverageOpen, setCoverageOpen] = useState(false)
  const countriesMeta = useCountriesMeta()

  const baseMeta = resolveMeta(codeParam)
  // Overlay the server's per-locale networks string once /api/countries
  // resolves. Until then we render whatever the static catalog gave us.
  const meta = baseMeta && countriesMeta && countriesMeta[baseMeta.code]
    ? { ...baseMeta, networks: countriesMeta[baseMeta.code].networks }
    : baseMeta

  const countryName = (code: string, fallback: string): string => {
    const v = tCommon(`countries.${code}`)
    return v === `countries.${code}` ? fallback : v
  }
  const regionalName = (code: string, fallback: string): string => {
    const v = tCommon(`regions.${code}`)
    return v === `regions.${code}` ? fallback : v
  }
  // Display name picks the right translation source by meta type.
  const displayName = meta
    ? meta.isRegional
      ? regionalName(meta.code, meta.name)
      : countryName(meta.code, meta.name)
    : ''

  useDocumentMeta({
    title: displayName ? t('seo.title', { country: displayName }) : t('seo.title', { country: '' }),
    description: displayName ? t('seo.description', { country: displayName }) : t('seo.description', { country: '' }),
  })

  useEffect(() => {
    if (meta) track('destination_view', { country: meta.code, name: meta.name })
  }, [meta?.code])

  const allPlans = useMemo(() => (plans && meta ? plansForCountry(plans, meta.code) : []), [plans, meta])
  const regularPlans = useMemo(() => allPlans.filter((p) => !isUnlimited(p)), [allPlans])
  const unlimitedPlans = useMemo(() => allPlans.filter(isUnlimited), [allPlans])
  const visible = tab === 'unlimited' ? unlimitedPlans : regularPlans

  // Get checkout's payment form loading before the click (lib/checkoutWarmup.ts):
  // a few seconds after this page has loaded, once idle (not on low-memory
  // phones), or as soon as a plan row is hovered, pressed or focused.
  useCheckoutWarmup(visible.length ? Math.min(...visible.map((p) => p.price_cents)) : undefined)

  // Per-route JSON-LD: a Product + AggregateOffer (when plans exist) and a
  // BreadcrumbList matching the on-page crumb. Injected client-side via
  // useStructuredData; Googlebot renders the SPA so it's read for rich
  // results. Computed before the early return so hook order stays stable.
  const jsonLd = useMemo(() => {
    if (!meta) return null
    const url = `${SITE_ORIGIN}${location.pathname}`
    const graph: Record<string, unknown>[] = [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: tCommon('nav.home'), item: `${SITE_ORIGIN}${withLang('/', lang)}` },
          { '@type': 'ListItem', position: 2, name: t('breadcrumb'), item: `${SITE_ORIGIN}${withLang('/destinations', lang)}` },
          { '@type': 'ListItem', position: 3, name: displayName, item: url },
        ],
      },
    ]
    if (allPlans.length > 0) {
      const lowCents = Math.min(...allPlans.map((p) => p.price_cents))
      graph.unshift({
        '@type': 'Product',
        name: t('seo.title', { country: displayName }),
        description: t('seo.description', { country: displayName }),
        // schema.org wants an absolute URL — locally-hosted artwork is a
        // site-relative path, so qualify it before it goes into the graph.
        ...(meta.image
          ? { image: meta.image.startsWith('/') ? `${SITE_ORIGIN}${meta.image}` : meta.image }
          : {}),
        category: meta.isRegional ? 'Regional travel eSIM' : 'Travel eSIM',
        brand: { '@id': `${SITE_ORIGIN}/#organization` },
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: priceDollars(lowCents),
          offerCount: allPlans.length,
          availability: 'https://schema.org/InStock',
          url,
          seller: { '@id': `${SITE_ORIGIN}/#organization` },
        },
      })
    }
    return { '@context': 'https://schema.org', '@graph': graph }
  }, [meta, allPlans, displayName, lang, location.pathname, t, tCommon])
  useStructuredData(jsonLd)

  if (!meta) {
    return (
      <div className="page-stub">
        <h1 className="h1">{t('notFound.title')}</h1>
        <p className="lede" style={{ marginTop: 12 }}>
          <Link to={withLang('/destinations', lang)} style={{ color: 'var(--ink)' }}>
            {t('notFound.browseAll')}
          </Link>
        </p>
      </div>
    )
  }

  const cheapest = allPlans[0]
  const longest = allPlans.length ? Math.max(...allPlans.map((p) => p.validity_days)) : null
  const shortest = allPlans.length ? Math.min(...allPlans.map((p) => p.validity_days)) : null

  // Real per-country coverage for regional bundles that have a footprint
  // defined (e.g. Europe). Drives the Coverage card + searchable modal.
  const coverageEntries = meta.isRegional ? REGION_COVERAGE[meta.code] : undefined
  // CHM covers mainland China plus two SARs, so its card/modal read "regions"
  // rather than "countries". Everything else keeps the country wording.
  const covKey = (base: string) => (meta.coverageUnit === 'regions' ? `${base}Regions` : base)

  const upsellRegionalCode = !meta.isRegional ? REGIONAL_UPSELL[meta.code] : undefined
  const upsellRegional = upsellRegionalCode
    ? REGIONAL_PLANS_META.find((r) => r.code === upsellRegionalCode)
    : undefined
  const upsellPitch = upsellRegionalCode ? t(`upsell.${meta.code}`) : ''

  const goCheckout = (planId: string) => {
    const p = visible.find((x) => x.id === planId)
    notePlanClick(planId)
    track('plan_clicked', {
      plan_id: planId,
      country: meta?.code,
      price_cents: p?.price_cents ?? null,
    })
    gtagConversion('plan_clicked')
    oaiqPlanClicked(p?.price_cents ?? null)
    navigate(withLang(`/checkout?plan=${encodeURIComponent(planId)}`, lang))
  }

  const validityText =
    shortest !== null && longest !== null
      ? shortest === longest
        ? t('facts.validitySame', { days: shortest })
        : t('facts.validityRange', { shortest, longest })
      : t('facts.validityNone')

  return (
    <>
      <section className="detail-hero">
        <div className="detail-hero-inner">
          <div>
            <div className="breadcrumb">
              <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>{tCommon('nav.home')}</Link> /{' '}
              <Link to={withLang('/destinations', lang)} style={{ color: 'var(--ink-3)' }}>{t('breadcrumb')}</Link> /{' '}
              <span style={{ color: 'var(--ink)' }}>{displayName}</span>
            </div>
            <h1 className={`detail-title ${displayName.length > 26 ? 'detail-title--long' : ''}`}>
              <span className="flag-big">{meta.flag}</span>
              {displayName}
            </h1>
            <p className="lede">
              {meta.isRegional
                ? meta.desc
                : t('leadCountry', {
                    networks: meta.networks,
                    speed: meta.speed ?? t('facts.speedValue'),
                  })}
            </p>
            {meta.network_note && (
              <p className="network-note">{meta.network_note}</p>
            )}

            <div className="detail-facts">
              <div className="detail-fact">
                <div className="label">{t('facts.from')}</div>
                <div className="val num">
                  {cheapest ? `$${priceDollars(cheapest.price_cents)}` : '—'}{' '}
                  {cheapest && (
                    <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
                      {t('facts.perData', { data: formatData(cheapest.data_gb) })}
                    </span>
                  )}
                </div>
              </div>
              <div className="detail-fact">
                <div className="label">{meta.isRegional ? t('facts.coverage') : t('facts.networks')}</div>
                <div className="val">
                  {coverageEntries ? (
                    <button type="button" className="detail-fact__link" onClick={() => setCoverageOpen(true)}>
                      {meta.scope} <Icon name="globe" size={13} />
                    </button>
                  ) : (
                    meta.isRegional ? meta.scope : meta.networks
                  )}
                </div>
              </div>
              <div className="detail-fact">
                <div className="label">{t('facts.speed')}</div>
                <div className="val">{meta.speed ?? t('facts.speedValue')}</div>
              </div>
              <div className="detail-fact">
                <div className="label">{t('facts.validity')}</div>
                <div className="val">{validityText}</div>
              </div>
            </div>
          </div>

          <div
            className="detail-artcard"
            style={
              meta.image
                ? {
                    // Finished artwork carries its own label and framing, so it
                    // gets no darkening scrim — that gradient exists to keep the
                    // overlaid label legible over a photo.
                    backgroundImage: meta.artwork
                      ? `url(${meta.image})`
                      : `linear-gradient(180deg, transparent 50%, rgba(11,31,58,.55) 100%), url(${meta.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : {
                    background:
                      meta.code === 'JP'
                        ? 'radial-gradient(circle at 30% 40%, #E85D3C 0 18%, transparent 19%), linear-gradient(180deg, #2C3E63 0%, #0B1F3A 100%)'
                        : meta.code === 'US'
                          ? 'linear-gradient(180deg, #3A5FAD 0%, #1E3566 100%)'
                          : meta.code === 'KR'
                            ? 'linear-gradient(180deg, #5068A8 0%, #1F2D5C 100%)'
                            : meta.code === 'CN' || meta.code === 'CHM'
                              ? 'linear-gradient(180deg, #C4333A 0%, #6A1820 100%)'
                              : meta.code === 'EU'
                                ? 'linear-gradient(180deg, #2B4A82 0%, #0E1F46 100%)'
                                : meta.code === 'AP'
                                  ? 'linear-gradient(180deg, #4A9E8A 0%, #1E4D44 100%)'
                                  : 'linear-gradient(180deg, #2C3E63 0%, #0B1F3A 100%)',
                  }
            }
          >
            {!meta.artwork && (
              <div className="lbl">
                <span>NIMVOY · {meta.code}</span>
                <span>{meta.flag}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="plans-section">
        <div className="plans-inner">
          <div className="plans-head plans-head--tabs-only">
            <div>
              <div className="eyebrow" style={{ marginBottom: 12 }}>{t('plans.eyebrow')}</div>
            </div>
            {unlimitedPlans.length > 0 && (
              <div className="plan-tabs">
                <button className={tab === 'regular' ? 'active' : ''} onClick={() => setTab('regular')}>
                  <span className="dot"></span> {t('plans.tabRegular')}
                </button>
                <button className={tab === 'unlimited' ? 'active' : ''} onClick={() => setTab('unlimited')}>
                  <span className="dot"></span> {t('plans.tabUnlimited')}
                </button>
              </div>
            )}
          </div>

          {loading && visible.length === 0 && (
            <div style={{ padding: 40, color: 'var(--ink-3)' }}>{t('plans.loading')}</div>
          )}

          {!loading && visible.length === 0 && (
            <div style={{ padding: 40, color: 'var(--ink-3)' }}>
              {tab === 'regular'
                ? t('plans.noneRegular', { name: displayName })
                : t('plans.noneUnlimited', { name: displayName })}
            </div>
          )}

          {visible.length > 0 && (
            <div className="plans-included" aria-label={t('plans.everyIncludesLabel')}>
              <span className="plans-included__lbl">{t('plans.everyIncludesLabel')}</span>
              <span className="plans-included__item">
                <Icon name="signal" size={14} /> {t('plans.feature4g5g')}
              </span>
              <span className="plans-included__item">
                <Icon name="hotspot" size={14} /> {t('plans.featureHotspot')}
              </span>
              <span className="plans-included__item">
                <Icon name="bolt" size={14} /> {t('plans.featureInstant')}
              </span>
            </div>
          )}

          {visible.length > 0 && (
            <button
              type="button"
              className="compat-strip"
              onClick={() => {
                track('compat_opened', { page: codeParam ?? 'unknown' })
                setCompatOpen(true)
              }}
            >
              <span className="compat-strip__icon"><Icon name="phone" size={14} /></span>
              <span className="compat-strip__copy">
                <strong>{t('plans.compatTitle')}</strong>
                <span className="compat-strip__sub">{t('plans.compatSub')}</span>
              </span>
              <span className="compat-strip__cta">
                {t('plans.compatCta')} <Icon name="arrow" size={12} />
              </span>
            </button>
          )}

          {(() => {
            const byDays = visible.reduce<Record<number, Plan[]>>((acc, p) => {
              acc[p.validity_days] = acc[p.validity_days] || []
              acc[p.validity_days].push(p)
              return acc
            }, {})
            const dayKeys = Object.keys(byDays).map(Number).sort((a, b) => a - b)
            return dayKeys.map((days) => {
              const rows = byDays[days].slice().sort((a, b) => a.data_gb - b.data_gb)
              return (
                <section key={days} className="plans-day">
                  <h3 className="plans-day__h">
                    {days === 1 ? t('plans.daysOne', { count: days }) : t('plans.daysOther', { count: days })}
                  </h3>
                  <div className="plans-day__rows">
                    {rows.map((p) => (
                      <PlanRow
                        key={p.id}
                        plan={p}
                        countryCode={meta.code}
                        unlimitedLabel={t('plans.rowUnlimited')}
                        gbLabel={(n) => t('plans.rowGb', { count: n })}
                        currency={t('plans.currency')}
                        onBuy={() => goCheckout(p.id)}
                        onIntent={() => warmCheckout(p.price_cents)}
                      />
                    ))}
                  </div>
                </section>
              )
            })
          })()}

          <div className={`detail-info ${meta.isRegional ? '' : 'detail-info--single'}`}>
            <div className="info-card">
              <h3>{t('info.goodToKnow')}</h3>
              <ul className="gtk-list">
                {([1, 2] as const).map((n) => (
                  <li key={n} className="gtk-row">
                    <span className="gtk-row__badge">{n}</span>
                    <span className="gtk-row__copy">
                      <strong>{t(`howItWorks.step${n}Lead`)}</strong>
                      <span>{t(`howItWorks.step${n}Body`)}</span>
                    </span>
                  </li>
                ))}
                {(
                  [
                    { icon: 'phone', label: 'info.callsLabel', value: 'info.callsValue' },
                    { icon: 'clock', label: 'info.activationLabel', value: 'info.activationValue' },
                    { icon: 'bolt', label: 'info.topupLabel', value: 'info.topupValue' },
                  ] as const
                ).map((f, i) => (
                  <li key={f.icon} className={i === 0 ? 'gtk-row gtk-row--group' : 'gtk-row'}>
                    <span className="gtk-row__badge"><Icon name={f.icon} size={12} /></span>
                    <span className="gtk-row__copy">
                      <strong>{t(f.label)}</strong>
                      <span>{t(f.value)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {coverageEntries && (
              <div className="info-card">
                <h3>{t('info.coverage')}</h3>
                <div className="cov-trigger__flags" aria-hidden>
                  {coverageEntries.slice(0, 12).map((c) => c.flag).join(' ')}
                </div>
                <div className="cov-trigger__count">
                  {t(`coverage.${covKey('count')}`, { count: coverageEntries.length })}
                </div>
                <button type="button" className="cov-trigger__btn" onClick={() => setCoverageOpen(true)}>
                  <Icon name="globe" size={15} />
                  {t(`coverage.${covKey('cta')}`)}
                  <Icon name="arrow" size={13} />
                </button>
              </div>
            )}
            {meta.isRegional && !coverageEntries && (
              <div className="info-card">
                <h3>{t('info.coverage')}</h3>
                <ul className="coverage-list">
                  <li>
                    <span className="flag">{meta.flag}</span>
                    <span>{meta.scope}</span>
                    <span className="net">{meta.networks}</span>
                  </li>
                </ul>
              </div>
            )}
            {!meta.isRegional && upsellRegional && (
              <div className="coverage-upsell coverage-upsell--inline">
                <p className="muted">{upsellPitch}</p>
                <Link
                  to={withLang(`/destinations/${upsellRegional.code.toLowerCase()}`, lang)}
                  className="coverage-upsell-cta"
                >
                  {t('upsell.tryThe', { name: regionalName(upsellRegional.code, upsellRegional.name) })}{' '}
                  <Icon name="arrow" size={12} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <CompatibilityModal open={compatOpen} onClose={() => setCompatOpen(false)} />
      {coverageEntries && (
        <CoverageModal
          open={coverageOpen}
          onClose={() => setCoverageOpen(false)}
          regionName={displayName}
          entries={coverageEntries}
          unit={meta.coverageUnit}
        />
      )}
    </>
  )
}

function PlanRow({
  plan,
  onBuy,
  onIntent,
  unlimitedLabel,
  gbLabel,
  currency,
}: {
  plan: Plan
  countryCode: string
  onBuy: () => void
  // Hover, press or focus: start warming checkout (see lib/checkoutWarmup.ts).
  onIntent: () => void
  unlimitedLabel: string
  gbLabel: (n: number) => string
  currency: string
}) {
  const unlimited = isUnlimited(plan)
  return (
    <button
      type="button"
      className="plan-row"
      onClick={onBuy}
      onPointerEnter={onIntent}
      onPointerDown={onIntent}
      onFocus={onIntent}
    >
      <span className="plan-row__data">
        {unlimited ? unlimitedLabel : gbLabel(plan.data_gb)}
      </span>
      <span className="plan-row__price-block">
        <span className="plan-row__price">
          <b>${priceDollars(plan.price_cents)}</b>
          <span className="plan-row__currency">{currency}</span>
        </span>
        <Icon name="arrow" size={14} />
      </span>
    </button>
  )
}

// ---------------------------------------------------------------- dispatcher
//
// The China landing-page rebuild originally shipped behind `?preview=1` so the
// page customers were using stayed untouched during review. Sitting here rather
// than in App.tsx means one edit covers all eight locale route trees.
//
// Deliberately NOT a separate URL: useDocumentMeta builds the canonical from
// location.pathname alone, so the preview self-canonicalises to the real China
// page — no duplicate-content risk, nothing to add to robots.txt or the
// sitemap, and nothing to unwind later.
//
// PROMOTED 2026-07-31: the rebuild is now the live China page in all eight
// locales. `?preview=0` still renders the previous page — kept as an escape
// hatch for comparing the two and as a one-URL rollback while the change is
// young. `?preview=1` also still resolves, so links shared during review
// don't break.
//
// To roll back everything: change `!== '0'` to `=== '1'`, ship. Nothing else
// moves. Once you're confident, delete this dispatcher and the `?preview=0`
// branch, and point the route straight at ChinaLanding.
export default function DestinationRoute() {
  const { country } = useParams<{ country: string }>()
  const [params] = useSearchParams()

  if (country?.toLowerCase() === 'cn' && params.get('preview') !== '0') {
    return <ChinaLanding />
  }
  return <DestinationDetail />
}
